import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { resolveUploadUrl } from "../../utils/apiClient";
import {
  getEmployees, 
  inviteEmployee, 
  cancelInvite,
  fetchJobTitles, 
  createJobTitle,
  updateEmployee,
  getAllPermissions,
  archiveEmployee,
  uploadEmployeeAvatar,
  createPlaceholderEmployee,
  updatePlaceholderEmployee,
  deletePlaceholderEmployee,
  batchUpdateEmployees,
  resendInvites,
  getDepartments,
  createDepartment,
  getProjectsForEmployee,
} from "../../services/peopleService";
import {
  getResources,
  createResource,
  updateResource,
  archiveResource,
  restoreResource,
  batchUpdateResources,
} from "../../services/resourceService";
import { getBookings } from "../../services/scheduleService";
import { getCustomFields } from "../../services/customFieldService";
import {
  getEmployeeEntitlements,
  createEntitlement,
  updateEntitlement,
  deleteEntitlement,
  generateDefaultEntitlements
} from "../../services/leaveEntitlementService";
import { getTimeOffTypes, createTimeOffType } from "../../services/timeOffService";
import { getTeamRoles, createTeamRole } from "../../services/teamRoleService";
import { useCurrentUser } from "../../context/UserContext";
import { useCurrency } from "../../hooks/useCurrency";
import { usePermissions } from "../../hooks/usePermission";
import FormModal from "../../components/forms/FormModal";
import EmployeeDetailModal from "./people/EmployeeDetailModal";
import ResourceDetailModal from "./people/ResourceDetailModal";
import PermissionSelector from "../../components/forms/PermissionSelector";
import ConfirmDialog from "../../components/ConfirmDialog";
import Toast from "../../components/Toast";
import ui from "./DashboardUI.module.css";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import peopleStyles from "./people/PeopleUI.module.css";
import PersonCompactCard from "./people/PersonCompactCard";
import ResourceCompactCard, { RESOURCE_ICON_CLASS } from "./people/ResourceCompactCard";
import ResourceTypeIcon from "./people/ResourceTypeIcon";
import EmptyContact from "./people/EmptyContact";
import WeekBar from "./people/WeekBar";
import ProfitCell from "./people/ProfitCell";
import CostBreakdown from "./people/CostBreakdown";
import { getRoleVariant } from "./people/roleVariant";
import {
  PEOPLE_COLUMNS,
  RESOURCE_COLUMNS,
  FINANCE_KEYS,
  RESOURCE_TYPES,
  RESOURCE_STATUSES,
  PEOPLE_GROUPS,
  RESOURCE_GROUPS,
  DEFAULT_FILTERS,
  resourceTypeLabel,
  resourceStatusLabel,
  resourceStatusColor,
  isInvite,
  personRowId,
  personRoleLabel,
  filterPeople,
  filterResources,
  sortRows,
} from "./people/peopleConfig";
import DataTable from "../../components/forms/DataTable";
import CustomDropdown from "../../components/forms/CustomDropdown";
import SegmentedControl from "../../components/common/SegmentedControl";
import AddMenu from "../../components/common/AddMenu";
import BatchBar from "../../components/common/BatchBar";
import StatCard from "../../components/common/StatCard";
import Avatar from "../../components/common/Avatar";
import CustomFieldRows from "../../components/common/CustomFieldRows";
import DocumentsPanel from "../../components/common/DocumentsPanel";
import CustomFieldModal from "../../components/settings/CustomFieldModal";
import chrome from "../../components/common/PageChrome.module.css";
import useTableColumns from "../../hooks/useTableColumns";
import useSelection from "../../hooks/useSelection";
import useBatchRunner from "../../hooks/useBatchRunner";
import { errorMessage } from "../../utils/errorMessage";
import { personCosts, resourceCosts, sumCosts, formatCompact } from "../../utils/costUtils";
import { customSortAccessor, customCellRenderers } from "../../utils/customFieldUtils";
import { toggleSort } from "../../utils/sortRows";

const ROLE_PILL = {
  owner: peopleStyles.rolePillOwner,
  employee: peopleStyles.rolePillEmployee,
  contractor: peopleStyles.rolePillContractor,
  invited: peopleStyles.rolePillInvited,
  role: peopleStyles.rolePillRole,
};

// Expired / expiring / on-file counts, for the Documents column.
const documentSummary = (row) => {
  const expired = Number(row.doc_expired) || 0;
  const expiring = Number(row.doc_expiring) || 0;
  const count = Number(row.doc_count) || 0;
  if (expired) return <span className={peopleStyles.docAlert}>{expired} expired</span>;
  if (expiring) return <span className={peopleStyles.docWarn}>{expiring} expiring</span>;
  return count ? `${count} on file` : "—";
};

export default function People() {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { symbol: currencySymbol, format: formatMoney } = useCurrency();
  const { canInvitePeople, canEditPeople, canEditPermissions, canDeletePeople, canViewResources, canEditResources, canDeleteResources, canAccessTimeOff, canViewProjectBudgets, canManageCompanySettings } = usePermissions();
  const [users, setUsers] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [toast, setToast] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelInviteConfirm, setShowCancelInviteConfirm] = useState(false);

  // Leave entitlement state
  const [editTab, setEditTab] = useState('details');
  const [entitlements, setEntitlements] = useState([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [showAddEntitlementModal, setShowAddEntitlementModal] = useState(false);
  const [editingEntitlement, setEditingEntitlement] = useState(null);
  const [showNewLeaveTypeModal, setShowNewLeaveTypeModal] = useState(false);
  const [newLeaveTypeForm, setNewLeaveTypeForm] = useState({ name: '', description: '', default_allocation: '' });
  const [entitlementForm, setEntitlementForm] = useState({
    time_off_type_id: '',
    total_days: '',
    carry_over_days: '0',
    period_start: `${new Date().getFullYear()}-01-01`,
    period_end: `${new Date().getFullYear()}-12-31`,
    notes: ''
  });

  // Resource state
  const [resources, setResources] = useState([]);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showResourceDetailModal, setShowResourceDetailModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    name: "",
    type: "misc",
    description: "",
    availability_status: "available",
    asset_value: "",
    running_cost: "",
    depreciation_rate: "",
    cost_center: "",
    registration_number: "",
    vehicle_type: "",
    capacity: "",
    insurance_expiry: "",
    inspection_expiry: "",
    bill_rate_day: "",
    assigned_employee_id: "",
  });

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showNewJobTitleModal, setShowNewJobTitleModal] = useState(false);
  const [newJobTitleName, setNewJobTitleName] = useState("");
  const [jobTitleTarget, setJobTitleTarget] = useState(null); // 'invite' or 'edit'
  const [avatarFile, setAvatarFile] = useState(null); // For editing employee avatar
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone_number: "",
    default_hourly_rate: "",
    default_weekly_hours: "",
    title_id: "",
    permissions: [],
  });
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [showAdvancedPermissions, setShowAdvancedPermissions] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [newRoleForm, setNewRoleForm] = useState({
    role_name: "",
    description: "",
    permissions: [],
  });
  const [placeholderForm, setPlaceholderForm] = useState({
    name: "",
    phone_number: "",
    description: "",
    default_hourly_rate: "",
    default_weekly_hours: "",
  });
  const [editingPlaceholder, setEditingPlaceholder] = useState(null);
  const [placeholderTab, setPlaceholderTab] = useState('details');
  const [resourceTab, setResourceTab] = useState('details');
  const [employeeProjects, setEmployeeProjects] = useState(null);
  const [resourceBookings, setResourceBookings] = useState(null);
  const [departmentTarget, setDepartmentTarget] = useState(null); // setter for the form that asked for a new department
  const [newDepartmentName, setNewDepartmentName] = useState("");

  // Tabs, per-tab filters, columns and selection. The last tab and view are remembered per browser.
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('peoplePageTab') || 'people'; }
    catch { return 'people'; }
  });
  const activeTab = canViewResources ? tab : 'people';
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('peoplePageViewMode') || 'list'; }
    catch { return 'list'; }
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [collapsedGroups, setCollapsedGroups] = useState({ people: {}, resources: {} });
  const [departments, setDepartments] = useState([]);
  const [personFields, setPersonFields] = useState([]);
  const [resourceFields, setResourceFields] = useState([]);
  const [addFieldEntity, setAddFieldEntity] = useState(null);
  const peopleColumns = useTableColumns('peopleColumns', PEOPLE_COLUMNS, personFields);
  const resourceColumns = useTableColumns('resourceColumns', RESOURCE_COLUMNS, resourceFields);
  const peopleSelection = useSelection();
  const resourceSelection = useSelection();

  useEffect(() => {
    try { localStorage.setItem('peoplePageViewMode', viewMode); } catch {}
  }, [viewMode]);

  useEffect(() => {
    try { localStorage.setItem('peoplePageTab', tab); } catch {}
  }, [tab]);

  const setFilter = (patch) =>
    setFilters((prev) => ({ ...prev, [activeTab]: { ...prev[activeTab], ...patch } }));
  const clearFilters = () => setFilters((prev) => ({ ...prev, [activeTab]: DEFAULT_FILTERS[activeTab] }));

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Direct edit/delete helpers used by row action menus (skip detail modal)
  const editEmployeeDirect = (emp) => {
    if (!emp) return;
    if (emp.kind === 'ghost') {
      setEditingPlaceholder(emp);
      return;
    }
    const [firstName = '', lastName = ''] = (emp.name || '').split(' ');
    setEditingEmployee({
      ...emp,
      firstName,
      lastName,
      permissions: emp.permissions || [],
    });
    setEditTab('details');
    setShowAdvancedPermissions(false);
  };

  // Map backend status -> frontend 'kind' used for filtering. Archived
  // employees keep their real/ghost kind (and the is_active flag) so they
  // can still be shown and restored.
  const mapEmployees = (list) => (list || []).map((emp) => ({
    ...emp,
    id: personRowId(emp),
    kind: emp.status === 'invited'
      ? 'invited'
      : (emp.kind === 'ghost' || emp.status === 'placeholder')
        ? 'ghost'
        : 'real',
  }));
  const mapResources = (list) => (list || []).map((r) => ({ ...r, id: r.resource_id }));

  // Archived rows are always loaded; the Active / Archived switch filters client-side.
  const refreshEmployees = async () => {
    const updated = await getEmployees(true);
    setUsers(mapEmployees(updated?.employees || []));
  };

  useEffect(() => {
    async function loadData() {
      if (!currentUser) return;

      try {
        const [employeesData, titlesData, permissionsData, rolesData, departmentsData, personFieldsData] = await Promise.all([
          getEmployees(true),
          fetchJobTitles(),
          canEditPermissions ? getAllPermissions() : Promise.resolve({ permissions: {} }),
          getTeamRoles().catch(() => []),
          getDepartments().catch(() => []),
          getCustomFields('employee').catch(() => ({ fields: [] })),
        ]);

        setUsers(mapEmployees(employeesData?.employees || []));
        setJobTitles(titlesData || []);
        setAvailablePermissions(permissionsData?.permissions || {});
        setTeamRoles(rolesData || []);
        setDepartments(departmentsData || []);
        setPersonFields(personFieldsData?.fields || []);

        // Load resources if user has permission
        if (canViewResources) {
          try {
            const [resourcesData, resourceFieldsData] = await Promise.all([
              getResources(true),
              getCustomFields('resource').catch(() => ({ fields: [] })),
            ]);
            setResources(mapResources(resourcesData?.resources));
            setResourceFields(resourceFieldsData?.fields || []);
          } catch (err) {
            console.error('Error loading resources:', err);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [currentUser, canInvitePeople, canEditPermissions, canViewResources]);

  // Load time off types once
  useEffect(() => {
    if (canAccessTimeOff) {
      getTimeOffTypes().then(data => setTimeOffTypes(data || [])).catch(console.error);
    }
  }, [canAccessTimeOff]);

  // Fetch entitlements when employee detail modal opens on Time Off tab
  const fetchEntitlements = async (employeeId) => {
    setEntitlementsLoading(true);
    try {
      const data = await getEmployeeEntitlements(employeeId);
      setEntitlements(data || []);
    } catch (err) {
      console.error('Error loading entitlements:', err);
      setEntitlements([]);
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleEditTabChange = (tab) => {
    setEditTab(tab);
    if (tab === 'timeoff' && editingEmployee) {
      fetchEntitlements(editingEmployee.user_id);
    }
    if (tab === 'projects' && editingEmployee) {
      setEmployeeProjects(null);
      getProjectsForEmployee(editingEmployee.user_id).then(setEmployeeProjects).catch(() => setEmployeeProjects([]));
    }
  };

  // Bookings tab: the next 60 days of schedule entries for this resource.
  const handleResourceTabChange = (tab) => {
    setResourceTab(tab);
    if (tab === 'bookings' && editingResource) {
      const from = new Date();
      const to = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
      setResourceBookings(null);
      getBookings(from.toISOString().slice(0, 10), to.toISOString().slice(0, 10))
        .then((list) => setResourceBookings((list || [])
          .filter((b) => b.resource_id === editingResource.resource_id)
          .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))))
        .catch(() => setResourceBookings([]));
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (!newDepartmentName.trim()) {
      showToast("Department name is required.", "error");
      return;
    }
    try {
      const created = await createDepartment(newDepartmentName.trim());
      setDepartments((prev) => (prev.some((d) => d.department_id === created.department_id)
        ? prev
        : [...prev, created].sort((a, b) => a.name.localeCompare(b.name))));
      departmentTarget?.((prev) => ({ ...prev, department_id: created.department_id }));
      setDepartmentTarget(null);
      setNewDepartmentName("");
    } catch (err) {
      showToast(errorMessage(err, "Failed to create department."), "error");
    }
  };

  // Department select shared by the employee and contractor forms; "+ Add department…" opens a small form.
  const departmentField = (formState, setFormState) => ({
    label: "Department",
    type: "select",
    value: formState.department_id || "",
    onChange: (e) => {
      if (e.target.value === "add-new-department") setDepartmentTarget(() => setFormState);
      else setFormState((prev) => ({ ...prev, department_id: e.target.value ? Number(e.target.value) : null }));
    },
    options: [
      { value: "", label: "No department" },
      { value: "add-new-department", label: "+ Add department…" },
      ...departments.map((d) => ({ value: d.department_id, label: d.name })),
    ],
  });

  // Inline custom field edits save straight away, like on Leads and Projects.
  const saveEmployeeField = async (employee, field, value) => {
    try {
      const custom_fields = { [field.field_id]: value };
      if (employee.kind === 'ghost') await updatePlaceholderEmployee(employee.user_id, { custom_fields });
      else await updateEmployee({ account_user_id: employee.account_user_id, custom_fields });
      const setter = employee.kind === 'ghost' ? setEditingPlaceholder : setEditingEmployee;
      setter((prev) => ({ ...prev, custom_fields: { ...prev.custom_fields, [field.field_id]: value } }));
      await refreshEmployees();
    } catch (err) {
      showToast(errorMessage(err, "Failed to save."), "error");
    }
  };

  const saveResourceField = async (field, value) => {
    try {
      // The PUT overwrites every column, so send the saved row, not the form's unsaved edits.
      const { custom_fields: _ignored, ...saved } = resources.find((r) => r.resource_id === editingResource.resource_id);
      await updateResource(editingResource.resource_id, { ...saved, custom_fields: { [field.field_id]: value } });
      setEditingResource((prev) => ({ ...prev, custom_fields: { ...prev.custom_fields, [field.field_id]: value } }));
      await refreshResources();
    } catch (err) {
      showToast(errorMessage(err, "Failed to save."), "error");
    }
  };

  const handleGenerateDefaults = async () => {
    if (!editingEmployee) return;
    try {
      await generateDefaultEntitlements(editingEmployee.user_id);
      await fetchEntitlements(editingEmployee.user_id);
      showToast('Default entitlements generated successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to generate defaults.', 'error');
    }
  };

  const handleAddEntitlement = async (e) => {
    e.preventDefault();
    if (!editingEmployee) return;
    try {
      await createEntitlement({
        employee_id: editingEmployee.user_id,
        time_off_type_id: entitlementForm.time_off_type_id,
        total_days: parseFloat(entitlementForm.total_days) || 0,
        carry_over_days: parseFloat(entitlementForm.carry_over_days) || 0,
        period_start: entitlementForm.period_start,
        period_end: entitlementForm.period_end,
        notes: entitlementForm.notes
      });
      await fetchEntitlements(editingEmployee.user_id);
      setShowAddEntitlementModal(false);
      setEntitlementForm({
        time_off_type_id: '',
        total_days: '',
        carry_over_days: '0',
        period_start: `${new Date().getFullYear()}-01-01`,
        period_end: `${new Date().getFullYear()}-12-31`,
        notes: ''
      });
      showToast('Entitlement added successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.error || err.message || 'Failed to add entitlement.', 'error');
    }
  };

  const handleUpdateEntitlement = async (e) => {
    e.preventDefault();
    if (!editingEntitlement || !editingEmployee) return;
    try {
      await updateEntitlement(editingEntitlement.entitlement_id, {
        total_days: parseFloat(editingEntitlement.total_days) || 0,
        carry_over_days: parseFloat(editingEntitlement.carry_over_days) || 0,
        period_start: editingEntitlement.period_start,
        period_end: editingEntitlement.period_end,
        notes: editingEntitlement.notes
      });
      await fetchEntitlements(editingEmployee.user_id);
      setEditingEntitlement(null);
      showToast('Entitlement updated successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to update entitlement.', 'error');
    }
  };

  const handleDeleteEntitlement = async (entitlementId) => {
    if (!editingEmployee) return;
    try {
      await deleteEntitlement(entitlementId);
      await fetchEntitlements(editingEmployee.user_id);
      showToast('Entitlement deleted successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to delete entitlement.', 'error');
    }
  };

  const handleCreateLeaveType = async (e) => {
    e.preventDefault();
    if (!newLeaveTypeForm.name.trim()) {
      showToast('Leave type name is required.', 'error');
      return;
    }
    try {
      await createTimeOffType({
        name: newLeaveTypeForm.name.trim(),
        description: newLeaveTypeForm.description.trim(),
        default_allocation: newLeaveTypeForm.default_allocation ? parseInt(newLeaveTypeForm.default_allocation) : null
      });
      const updated = await getTimeOffTypes();
      setTimeOffTypes(updated || []);
      setShowNewLeaveTypeModal(false);
      setNewLeaveTypeForm({ name: '', description: '', default_allocation: '' });
      showToast('Leave type created successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to create leave type.', 'error');
    }
  };

  const handleTeamRoleChange = (roleId) => {
    if (!roleId) {
      return;
    }

    // Check if user wants to create a new role
    if (roleId === "create-new-role") {
      setShowCreateRoleModal(true);
      return;
    }

    const selectedRole = teamRoles.find(role => role.role_id === parseInt(roleId));
    if (selectedRole && selectedRole.permissions) {
      setForm((prev) => ({ ...prev, permissions: selectedRole.permissions }));
    }
  };

  const handleEditTeamRoleChange = (roleId) => {
    if (!roleId) {
      return;
    }

    if (roleId === "create-new-role") {
      setShowCreateRoleModal(true);
      return;
    }

    const selectedRole = teamRoles.find(role => role.role_id === parseInt(roleId));
    if (selectedRole) {
      setEditingEmployee((prev) => ({ ...prev, team_role_id: selectedRole.role_id, permissions: selectedRole.permissions || prev.permissions }));
    }
  };

  const handleCreateNewRole = async (e) => {
    e.preventDefault();
    
    if (!newRoleForm.role_name || newRoleForm.role_name.trim() === "") {
      showToast("Please enter a role name.", "error");
      return;
    }

    try {
      const roleData = {
        role_name: newRoleForm.role_name,
        description: newRoleForm.description,
        permissions: newRoleForm.permissions || [],
        is_default: false
      };

      const createdRole = await createTeamRole(roleData);
      showToast(`Role "${createdRole.role_name}" created successfully!`, "success");
      
      // Refresh team roles list
      const rolesData = await getTeamRoles();
      setTeamRoles(rolesData || []);
      
      // Apply the new role's permissions to the invite form
      setForm((prev) => ({ ...prev, permissions: createdRole.permissions || [] }));
      
      // Reset and close modal
      setNewRoleForm({
        role_name: "",
        description: "",
        permissions: [],
      });
      setShowCreateRoleModal(false);
    } catch (error) {
      showToast(error.message || "Failed to create role", "error");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();

    if (!form.email || form.email.trim() === "") {
      showToast("Please enter an email address.", "error");
      return;
    }

    if (!form.title_id || form.title_id === "") {
      showToast("Please select a job title.", "error");
      return;
    }

    const formData = { 
      ...form,
      default_hourly_rate: form.default_hourly_rate ? parseFloat(form.default_hourly_rate) : null,
      default_weekly_hours: form.default_weekly_hours ? parseFloat(form.default_weekly_hours) : 40,
      permissions: form.permissions || [],
    };     

    try {
      
      setShowInviteForm(false);
      await inviteEmployee(formData);

      showToast("Employee invitation sent successfully.", "success");

      await refreshEmployees();

      setForm({
        firstName: "",
        lastName: "",
        email: "",
        title_id: "",
        permissions: [],
      });

      setShowInviteForm(false);
      showToast("Employee invitation sent successfully.", "success");

    } catch (err) {
      console.error(err);
      const errorMessage = err.message || err.error || "Failed to send invite.";
      showToast(errorMessage, "error");
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      const { custom_fields: _ignored, ...employeeFields } = editingEmployee;
      const updatedEmployee = {
        ...employeeFields,
        name: `${editingEmployee.firstName} ${editingEmployee.lastName}`.trim(),
        default_hourly_rate: editingEmployee.default_hourly_rate === '' || editingEmployee.default_hourly_rate === null || editingEmployee.default_hourly_rate === undefined
          ? null
          : parseFloat(editingEmployee.default_hourly_rate),
        default_weekly_hours: editingEmployee.default_weekly_hours === '' || editingEmployee.default_weekly_hours === null || editingEmployee.default_weekly_hours === undefined
          ? null
          : parseFloat(editingEmployee.default_weekly_hours),
      };

      await updateEmployee(updatedEmployee);

      // Upload avatar if a file was selected
      if (avatarFile) {
        await uploadEmployeeAvatar(editingEmployee.account_user_id, avatarFile);
      }

      await refreshEmployees();
      setEditingEmployee(null);
      setAvatarFile(null); // Reset avatar file
      setShowAdvancedPermissions(false); // Reset advanced permissions state
      showToast("Employee updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update employee.", "error");
    }
  };

  const openEmployeeDetail = (employee) => {
    setSelectedEmployee(employee);
    setEntitlements([]);
    setShowDetailModal(true);
  };

  const handleEditFromDetail = () => {
    if (selectedEmployee) {
      const [firstName = "", lastName = ""] = selectedEmployee.name.split(" ");
      setEditingEmployee({ 
        ...selectedEmployee, 
        firstName, 
        lastName,
        permissions: selectedEmployee.permissions || []
      });
      setEditTab('details');
      setShowAdvancedPermissions(false); // Reset advanced permissions state
      setShowDetailModal(false);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    
    if (selectedEmployee.kind === 'ghost') {
      await handleDeletePlaceholder();
      return;
    }

    try {
      await archiveEmployee(selectedEmployee.user_id);

      await refreshEmployees();

      setShowDeleteConfirm(false);
      setShowDetailModal(false);
      setSelectedEmployee(null);
      showToast("Employee archived successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to archive employee.", "error");
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelInvite = async () => {
    if (!selectedEmployee || !selectedEmployee.invite_id) return;
    
    try {
      await cancelInvite(selectedEmployee.invite_id);
      
      await refreshEmployees();
      
      setShowCancelInviteConfirm(false);
      setShowDetailModal(false);
      setSelectedEmployee(null);
      showToast("Invitation cancelled successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to cancel invitation.", "error");
      setShowCancelInviteConfirm(false);
    }
  };

  // Handle create placeholder
  const handleCreatePlaceholder = async (e) => {
    e.preventDefault();
    if (!placeholderForm.name.trim()) {
      showToast("Name is required for a contractor.", "error");
      return;
    }
    try {
      await createPlaceholderEmployee({
        name: placeholderForm.name.trim(),
        phone_number: placeholderForm.phone_number || null,
        description: placeholderForm.description || null,
        default_hourly_rate: placeholderForm.default_hourly_rate || null,
        default_weekly_hours: placeholderForm.default_weekly_hours || null
      });
      setShowPlaceholderModal(false);
      setPlaceholderForm({
        name: "",
        phone_number: "",
        description: "",
        default_hourly_rate: "",
        default_weekly_hours: "",
      });
      await refreshEmployees();
      showToast("Contractor employee created successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to create contractor employee.", "error");
    }
  };

  const handleCreateJobTitle = async () => {
    const names = newJobTitleName
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const uniqueNames = [...new Set(names)];

    if (uniqueNames.length === 0) {
      showToast("Job title name is required.", "error");
      return;
    }

    const created = [];
    const failed = [];

    for (const name of uniqueNames) {
      try {
        const result = await createJobTitle(name);
        created.push(result);
      } catch (err) {
        console.error(err);
        failed.push({ name, message: err.message || 'Failed to create.' });
      }
    }

    if (created.length > 0) {
      setJobTitles((prev) => [...prev, ...created]);

      const lastCreated = created[created.length - 1];
      if (jobTitleTarget === 'invite') {
        setForm((prev) => ({ ...prev, title_id: lastCreated.title_id }));
      } else if (jobTitleTarget === 'edit') {
        setEditingEmployee((prev) => ({ ...prev, title_id: lastCreated.title_id }));
      }
    }

    if (failed.length === 0) {
      setShowNewJobTitleModal(false);
      setNewJobTitleName("");
      setJobTitleTarget(null);
      showToast(
        created.length === 1
          ? "Job title created successfully."
          : `${created.length} job titles created successfully.`,
        "success"
      );
    } else {
      // Keep modal open with the names that failed so the user can retry.
      setNewJobTitleName(failed.map((f) => f.name).join('\n'));
      showToast(
        `${created.length} created, ${failed.length} failed: ${failed[0].message}`,
        "error"
      );
    }
  };

  // Get job title name from title_id
  const getJobTitleName = (titleId) => {
    const title = jobTitles.find(jt => jt.title_id === titleId);
    return title ? title.name : "—";
  };

  // Handle edit placeholder from detail
  const handleEditPlaceholderFromDetail = () => {
    if (selectedEmployee && selectedEmployee.kind === 'ghost') {
      setEditingPlaceholder(selectedEmployee);
      setShowDetailModal(false);
    }
  };

  // Handle update placeholder
  const handleUpdatePlaceholder = async (e) => {
    e.preventDefault();
    if (!editingPlaceholder.name.trim()) {
      showToast("Name is required.", "error");
      return;
    }
    try {
      await updatePlaceholderEmployee(editingPlaceholder.user_id, {
        name: editingPlaceholder.name.trim(),
        phone_number: editingPlaceholder.phone_number || null,
        description: editingPlaceholder.description || null,
        default_hourly_rate: editingPlaceholder.default_hourly_rate || null,
        default_weekly_hours: editingPlaceholder.default_weekly_hours || null,
        bill_rate: editingPlaceholder.bill_rate || null,
        department_id: editingPlaceholder.department_id || null,
      });
      setEditingPlaceholder(null);
      setPlaceholderTab('details');
      await refreshEmployees();
      showToast("Contractor employee updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update contractor employee.", "error");
    }
  };

  // Handle archive placeholder
  const handleDeletePlaceholder = async () => {
    if (!selectedEmployee || selectedEmployee.kind !== 'ghost') return;
    try {
      await deletePlaceholderEmployee(selectedEmployee.user_id);
      await refreshEmployees();
      setShowDeleteConfirm(false);
      setShowDetailModal(false);
      setSelectedEmployee(null);
      showToast("Contractor archived successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to archive contractor.", "error");
      setShowDeleteConfirm(false);
    }
  };

  // ---- Resource Handlers ----
  const resetResourceForm = () => {
    setResourceForm({
      name: "",
      type: "misc",
      description: "",
      availability_status: "available",
      asset_value: "",
      running_cost: "",
      depreciation_rate: "",
      cost_center: "",
      registration_number: "",
      vehicle_type: "",
      capacity: "",
      insurance_expiry: "",
      inspection_expiry: "",
    });
  };

  const refreshResources = async () => {
    try {
      const resourcesData = await getResources(true);
      setResources(mapResources(resourcesData?.resources));
    } catch (err) {
      console.error('Error refreshing resources:', err);
    }
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    if (!resourceForm.name.trim()) {
      showToast("Resource name is required.", "error");
      return;
    }
    try {
      await createResource(resourceForm);
      setShowResourceModal(false);
      resetResourceForm();
      await refreshResources();
      showToast("Resource created successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to create resource.", "error");
    }
  };

  const handleUpdateResource = async (e) => {
    e.preventDefault();
    if (!editingResource || !editingResource.name.trim()) {
      showToast("Resource name is required.", "error");
      return;
    }
    try {
      const { custom_fields: _ignored, ...resourceFields } = editingResource;
      await updateResource(editingResource.resource_id, resourceFields);
      setResourceTab('details');
      setEditingResource(null);
      await refreshResources();
      showToast("Resource updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update resource.", "error");
    }
  };

  const handleArchiveResource = async () => {
    if (!selectedResource) return;
    try {
      await archiveResource(selectedResource.resource_id);
      setShowArchiveConfirm(false);
      setShowResourceDetailModal(false);
      setSelectedResource(null);
      await refreshResources();
      showToast("Resource archived successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to archive resource.", "error");
      setShowArchiveConfirm(false);
    }
  };

  const handleRestoreResource = async (resource) => {
    try {
      await restoreResource(resource.resource_id);
      await refreshResources();
      showToast("Resource restored successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to restore resource.", "error");
    }
  };

  const openResourceDetail = (resource) => {
    setSelectedResource(resource);
    setShowResourceDetailModal(true);
  };

  const handleEditResourceFromDetail = () => {
    if (selectedResource) {
      setEditingResource({ ...selectedResource });
      setShowResourceDetailModal(false);
    }
  };

  const getResourceFormFields = (formState, setFormState) => {
    const fields = [
      {
        label: "Name",
        value: formState.name,
        onChange: (e) => setFormState((prev) => ({ ...prev, name: e.target.value })),
        placeholder: "Enter resource name...",
      },
      {
        label: "Type",
        type: "select",
        value: formState.type,
        onChange: (e) => setFormState((prev) => ({ ...prev, type: e.target.value })),
        options: [
          { value: "misc", label: "Miscellaneous" },
          { value: "vehicle", label: "Vehicle" },
          { value: "tool", label: "Tool / Equipment" },
        ],
      },
      {
        label: "Availability",
        type: "select",
        value: formState.availability_status,
        onChange: (e) => setFormState((prev) => ({ ...prev, availability_status: e.target.value })),
        options: [
          { value: "available", label: "Available" },
          { value: "in_use", label: "In Use" },
          { value: "maintenance", label: "Maintenance" },
          { value: "unavailable", label: "Unavailable" },
        ],
      },
      {
        label: "Assigned to",
        type: "select",
        value: formState.assigned_employee_id || "",
        onChange: (e) => setFormState((prev) => ({ ...prev, assigned_employee_id: e.target.value ? Number(e.target.value) : null })),
        options: [{ value: "", label: "Nobody" }, ...assignableOptions],
      },
      {
        label: "Description",
        type: "textarea",
        value: formState.description,
        onChange: (e) => setFormState((prev) => ({ ...prev, description: e.target.value })),
        placeholder: "Enter description...",
      },
    ];

    // Vehicle-specific fields
    if (formState.type === "vehicle") {
      fields.push(
        { type: "divider", label: "Vehicle Details" },
        {
          label: "Registration Number",
          value: formState.registration_number,
          onChange: (e) => setFormState((prev) => ({ ...prev, registration_number: e.target.value })),
          placeholder: "e.g., ABC 123 GP",
        },
        {
          label: "Vehicle Type",
          type: "select",
          value: formState.vehicle_type,
          onChange: (e) => setFormState((prev) => ({ ...prev, vehicle_type: e.target.value })),
          options: [
            { value: "", label: "Select vehicle type..." },
            { value: "truck", label: "Truck" },
            { value: "bakkie", label: "Bakkie" },
            { value: "van", label: "Van" },
            { value: "car", label: "Car" },
            { value: "trailer", label: "Trailer" },
            { value: "other", label: "Other" },
          ],
        },
        {
          label: "Capacity",
          value: formState.capacity,
          onChange: (e) => setFormState((prev) => ({ ...prev, capacity: e.target.value })),
          placeholder: "e.g., 5 tons, 8 passengers",
        },
        {
          type: "row",
          fields: [
            {
              label: "Insurance Expiry",
              type: "date",
              value: formState.insurance_expiry ? formState.insurance_expiry.split('T')[0] : "",
              onChange: (e) => setFormState((prev) => ({ ...prev, insurance_expiry: e.target.value })),
            },
            {
              label: "Inspection Expiry",
              type: "date",
              value: formState.inspection_expiry ? formState.inspection_expiry.split('T')[0] : "",
              onChange: (e) => setFormState((prev) => ({ ...prev, inspection_expiry: e.target.value })),
            },
          ],
        }
      );
    }

    // Accounting fields
    fields.push(
      { type: "divider", label: "Accounting" },
      {
        type: "row",
        fields: [
          {
            label: `Asset Value (${currencySymbol})`,
            type: "number",
            step: "0.01",
            min: "0",
            placeholder: "0.00",
            value: formState.asset_value,
            onChange: (e) => setFormState((prev) => ({ ...prev, asset_value: e.target.value })),
          },
          {
            label: `Running Cost / month (${currencySymbol})`,
            type: "number",
            step: "0.01",
            min: "0",
            placeholder: "0.00",
            value: formState.running_cost,
            onChange: (e) => setFormState((prev) => ({ ...prev, running_cost: e.target.value })),
          },
          {
            label: `Rate to clients / day (${currencySymbol})`,
            type: "number",
            step: "0.01",
            min: "0",
            placeholder: "0.00",
            value: formState.bill_rate_day || "",
            onChange: (e) => setFormState((prev) => ({ ...prev, bill_rate_day: e.target.value })),
          },
        ],
      },
      {
        type: "row",
        fields: [
          {
            label: "Depreciation Rate (%)",
            type: "number",
            step: "0.01",
            min: "0",
            max: "100",
            placeholder: "0.00",
            value: formState.depreciation_rate,
            onChange: (e) => setFormState((prev) => ({ ...prev, depreciation_rate: e.target.value })),
          },
          {
            label: "Cost Center",
            value: formState.cost_center,
            onChange: (e) => setFormState((prev) => ({ ...prev, cost_center: e.target.value })),
            placeholder: "e.g., Operations",
          },
        ],
      }
    );

    if (formState === editingResource && resourceFields.length > 0) {
      fields.push({ type: "divider", label: "Details" }, {
        type: "custom",
        render: () => (
          <CustomFieldRows fields={resourceFields} entity={editingResource} disabled={!canEditResources} formatMoney={formatMoney} onSave={saveResourceField} />
        ),
      });
    }

    return fields;
  };

  // ---- Active tab: rows, columns, grouping ----

  const isPeople = activeTab === 'people';
  const f = filters[activeTab];
  const rows = isPeople
    ? sortRows(filterPeople(users, f), f.sort, 'people', customSortAccessor(personFields))
    : sortRows(filterResources(resources, f), f.sort, 'resources', customSortAccessor(resourceFields));
  const activePeopleCount = users.filter((p) => isInvite(p) || p.is_active !== false).length;
  const activeResourceCount = resources.filter((r) => r.is_active !== false).length;
  const selection = isPeople ? peopleSelection : resourceSelection;
  const columnState = isPeople ? peopleColumns : resourceColumns;
  const shownColumns = columnState.columns.filter((c) => canViewProjectBudgets || !FINANCE_KEYS.has(c.key));
  const groups = isPeople ? PEOPLE_GROUPS : RESOURCE_GROUPS;
  const group = groups[f.groupBy] || null;
  const groupColor = group?.color ? (name) => group.color[name] || '#94a3b8' : null;
  const canSelect = isPeople ? (canEditPermissions || canDeletePeople || canInvitePeople) : (canEditResources || canDeleteResources);
  const roleOptions = [...new Set(users.map(personRoleLabel))].sort().map((r) => ({ value: r, label: r }));
  const assignableOptions = users
    .filter((p) => !isInvite(p) && p.is_active !== false)
    .map((p) => ({ value: String(p.user_id), label: p.name }));

  const handleSort = (key) => setFilter({ sort: toggleSort(f.sort, key) });

  const personCells = {
    name: (p) => (
      <span className={chrome.nameCell}>
        <Avatar name={p.name} imageUrl={p.avatar_url} size="sm" variant={p.kind === 'ghost' ? 'contractor' : 'default'} />
        <span>{p.name}</span>
      </span>
    ),
    role: (p) => <span className={`${peopleStyles.rolePill} ${ROLE_PILL[getRoleVariant(p)] || ''}`}>{personRoleLabel(p)}</span>,
    email: (p) => (p.email
      ? <a className={chrome.emailLink} href={`mailto:${p.email}`} onClick={(e) => e.stopPropagation()}>{p.email}</a>
      : p.phone_number ? '—' : <EmptyContact employee={p} onAddContactInfo={canEditPeople && !isInvite(p) ? editEmployeeDirect : null} />),
    phone: (p) => p.phone_number || '—',
    department: (p) => p.department || '—',
    week: (p) => (isInvite(p) ? '—' : (
      <span
        role="link"
        tabIndex={0}
        title="Open on Schedule"
        style={{ cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/schedule?open=${encodeURIComponent(p.name)}`); }}
        onKeyDown={(e) => e.key === 'Enter' && navigate(`/dashboard/schedule?open=${encodeURIComponent(p.name)}`)}
      >
        <WeekBar booked={p.week_booked_hours} capacity={p.default_weekly_hours} />
      </span>
    )),
    capacity: (p) => (p.default_weekly_hours ? `${Number(p.default_weekly_hours)}h / wk` : '—'),
    cost_rate: (p) => (p.default_hourly_rate ? `${formatMoney(p.default_hourly_rate)}/h` : '—'),
    profit: (p) => (isInvite(p) ? '—' : <ProfitCell costs={personCosts(p)} symbol={currencySymbol} />),
    leave_left: (p) => (p.leave_left != null ? `${Number(p.leave_left)} days` : '—'),
    projects: (p) => Number(p.project_count) || '—',
    documents: documentSummary,
    ...customCellRenderers(personFields, formatMoney),
  };

  const resourceCells = {
    name: (r) => (
      <span className={chrome.nameCell}>
        <span className={`${peopleStyles.resourceIcon} ${RESOURCE_ICON_CLASS[r.type] || ''} ${peopleStyles.resourceIconSm}`} aria-hidden="true">
          <ResourceTypeIcon type={r.type} />
        </span>
        <span>{r.name}</span>
      </span>
    ),
    type: (r) => resourceTypeLabel(r.type),
    status: (r) => (canEditResources && r.is_active !== false ? (
      <span onClick={(e) => e.stopPropagation()}>
        <CustomDropdown
          compact
          options={RESOURCE_STATUSES}
          value={r.availability_status}
          onChange={(v) => v !== r.availability_status
            && changeResources([r.resource_id], { availability_status: v }, `${r.name} set to ${resourceStatusLabel(v)}.`)}
        />
      </span>
    ) : resourceStatusLabel(r.availability_status)),
    assigned: (r) => (r.holder_name
      ? <span className={peopleStyles.assignedCell}><Avatar name={r.holder_name} size="sm" />{r.holder_name}</span>
      : '—'),
    profit: (r) => <ProfitCell costs={resourceCosts(r)} symbol={currencySymbol} />,
    reference: (r) => r.registration_number || '—',
    cost_center: (r) => r.cost_center || '—',
    next_booking: (r) => (r.next_booking
      ? new Date(r.next_booking).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
      : '—'),
    notes: (r) => r.description || '—',
    documents: documentSummary,
    ...customCellRenderers(resourceFields, formatMoney),
  };

  // ---- Batch changes (every reversible one offers Undo) ----
  const selectedPeople = users.filter((p) => peopleSelection.selectedIds.has(p.id));
  const selectedEmployeeIds = selectedPeople.filter((p) => !isInvite(p)).map((p) => p.user_id);
  const selectedInviteIds = selectedPeople.filter(isInvite).map((p) => p.invite_id);
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  const runBatch = useBatchRunner(setToast);

  const changePeopleRole = (roleId) => {
    const ids = selectedEmployeeIds;
    const role = teamRoles.find((r) => String(r.role_id) === String(roleId));
    peopleSelection.clear();
    runBatch(
      () => batchUpdateEmployees({ ids, team_role_id: Number(roleId) }),
      refreshEmployees,
      (res) => `${plural(res.updated.length, 'person', 'people')} set to ${role?.role_name}.`
        + (res.updated.length < ids.length ? ' Your own role was left as it is.' : ''),
      (res) => batchUpdateEmployees({ restore: res.previous }),
    );
  };

  const archivePeople = () => {
    const ids = selectedEmployeeIds;
    const archiving = !f.archived;
    peopleSelection.clear();
    runBatch(
      () => batchUpdateEmployees({ ids, archived: archiving }),
      refreshEmployees,
      (res) => `${plural(res.updated.length, 'person', 'people')} ${archiving ? 'archived' : 'restored'}.`
        + (archiving && res.updated.length < ids.length ? ' You can’t archive yourself.' : ''),
      (res) => batchUpdateEmployees({ ids: res.updated, archived: !archiving }),
    );
  };

  const resendSelectedInvites = () => {
    const ids = selectedInviteIds;
    peopleSelection.clear();
    runBatch(
      () => resendInvites(ids),
      refreshEmployees,
      (res) => `Resent ${plural(res.sent.length, 'invite', 'invites')}${res.failed ? `; ${res.failed} failed` : ''}.`,
    );
  };

  const changeResources = (ids, patch, message) => runBatch(
    () => batchUpdateResources({ ids, ...patch }),
    refreshResources,
    () => message,
    (res) => batchUpdateResources({ restore: res.previous }),
  );

  const batchResources = (patch, verb) => {
    const ids = [...resourceSelection.selectedIds];
    resourceSelection.clear();
    changeResources(ids, patch, `${plural(ids.length, 'resource', 'resources')} ${verb}.`);
  };

  // ---- Summary cards for the rows the current filter shows ----
  const costRows = isPeople ? rows.filter((p) => !isInvite(p)) : rows;
  const totals = sumCosts(costRows, isPeople ? personCosts : resourceCosts);
  const recovered = isPeople ? totals.costToProjects : totals.billed;
  const docTotals = costRows.reduce((acc, r) => ({
    expired: acc.expired + (Number(r.doc_expired) || 0),
    expiring: acc.expiring + (Number(r.doc_expiring) || 0),
    count: acc.count + (Number(r.doc_count) || 0),
  }), { expired: 0, expiring: 0, count: 0 });
  const money = (v) => formatCompact(currencySymbol, v);
  const pct = (v) => `${Math.round(v * 100)}%`;

  const gridGroups = group
    ? Object.entries(rows.reduce((acc, r) => {
        const name = group.get(r);
        (acc[name] = acc[name] || []).push(r);
        return acc;
      }, {})).sort(([a], [b]) => (group.order ? group.order.indexOf(a) - group.order.indexOf(b) : 0))
    : [[null, rows]];

  const filtersActive = f.search || f.role || f.department || f.type || f.status;
  const emptyMessage = filtersActive
    ? `No ${isPeople ? 'people' : 'resources'} match these filters.`
    : f.archived
      ? `No archived ${isPeople ? 'people' : 'resources'}.`
      : `No ${isPeople ? 'people' : 'resources'} yet.`;

  if (showLoading) {
    return (
      <div className={ui.pageContainer}>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            action={toast.action}
            onClose={() => setToast(null)}
          />
        )}
        <div className={ui.peopleSkeletonWrap}>
          <div className={ui.peopleSkeletonHeader}>
            <div className={ui.peopleSkeletonTitle} />
            <div className={ui.peopleSkeletonAddBtn} />
          </div>

          <div className={ui.peopleSkeletonSection}>
            <div className={ui.peopleSkeletonSectionHeader}>
              <div className={ui.peopleSkeletonSectionTitle} />
              <div className={ui.peopleSkeletonBadge} />
            </div>
            <div className={ui.peopleSkeletonCardGrid}>
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={`people-employee-card-skeleton-${idx}`} className={ui.peopleSkeletonCard}>
                  <div className={ui.peopleSkeletonAvatar} />
                  <div className={ui.peopleSkeletonLineLg} />
                  <div className={ui.peopleSkeletonLineMd} />
                </div>
              ))}
            </div>
          </div>

          <div className={ui.peopleSkeletonSection}>
            <div className={ui.peopleSkeletonSectionHeader}>
              <div className={ui.peopleSkeletonSectionTitle} />
              <div className={ui.peopleSkeletonBadge} />
            </div>
            <div className={ui.peopleSkeletonTableLike}>
              <div className={ui.peopleSkeletonTableHeader} />
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={`people-resource-row-skeleton-${idx}`} className={ui.peopleSkeletonTableRow} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.pageContainer}>
      <div className={ui.fadeInContent}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          action={toast.action}
          onClose={() => setToast(null)}
        />
      )}

      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft} style={{ alignItems: 'center' }}>
          <h1 className={ui.pageTitle}>People & Resources</h1>
          {canViewResources && (
            <SegmentedControl
              ariaLabel="People or resources"
              options={[
                { value: 'people', label: 'People', count: activePeopleCount },
                { value: 'resources', label: 'Resources', count: activeResourceCount },
              ]}
              value={activeTab}
              onChange={setTab}
            />
          )}
        </div>
        <div className={ui.pageHeaderRight}>
          <SegmentedControl
            ariaLabel="View mode"
            options={[{ value: 'list', label: 'List' }, { value: 'grid', label: 'Grid' }]}
            value={viewMode}
            onChange={setViewMode}
          />
          {canInvitePeople && (
            <AddMenu
              items={[
                { label: 'Employee', onClick: () => setShowInviteForm(true) },
                { label: 'Contractor', onClick: () => setShowPlaceholderModal(true) },
                ...(canViewResources ? [{ label: 'Resource', onClick: () => setShowResourceModal(true) }] : []),
              ]}
            />
          )}
        </div>
      </div>

      <div className={ui.tableToolbar}>
        <input
          type="text"
          className={ui.toolbarSearch}
          placeholder={isPeople ? 'Search people…' : 'Search resources…'}
          aria-label={isPeople ? 'Search people' : 'Search resources'}
          value={f.search}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
        {isPeople ? (
          <>
            <CustomDropdown
              toolbar
              options={[{ value: '', label: 'All Roles' }, ...roleOptions]}
              value={f.role}
              onChange={(v) => setFilter({ role: v })}
            />
            <CustomDropdown
              toolbar
              options={[{ value: '', label: 'All Departments' }, ...departments.map((d) => ({ value: d.name, label: d.name }))]}
              value={f.department}
              onChange={(v) => setFilter({ department: v })}
            />
          </>
        ) : (
          <>
            <CustomDropdown
              toolbar
              options={[{ value: '', label: 'All Types' }, ...RESOURCE_TYPES]}
              value={f.type}
              onChange={(v) => setFilter({ type: v })}
            />
            <CustomDropdown
              toolbar
              options={[{ value: '', label: 'All Statuses' }, ...RESOURCE_STATUSES]}
              value={f.status}
              onChange={(v) => setFilter({ status: v })}
            />
          </>
        )}
        <CustomDropdown
          toolbar
          options={[
            { value: '', label: 'No Grouping' },
            ...Object.entries(groups).map(([value, g]) => ({ value, label: `Group by ${g.label}` })),
          ]}
          value={f.groupBy}
          onChange={(v) => setFilter({ groupBy: v })}
        />
        <SegmentedControl
          ariaLabel="Active or archived"
          options={[{ value: 'active', label: 'Active' }, { value: 'archived', label: 'Archived' }]}
          value={f.archived ? 'archived' : 'active'}
          onChange={(v) => { selection.clear(); setFilter({ archived: v === 'archived' }); }}
        />
        <button type="button" className={ui.toolbarReset} onClick={clearFilters}>
          Clear
        </button>
      </div>

      <div className={chrome.statRow}>
        {canViewProjectBudgets && (
          <>
            <StatCard
              tone="primary"
              label="Cost to company"
              value={`${money(totals.costMonth)} / mo`}
              sub={isPeople ? 'Cost rate × capacity' : 'Running costs'}
            />
            <StatCard
              label={isPeople ? 'Cost to projects' : 'Billed to projects'}
              value={money(recovered)}
              sub={totals.costMonth > 0 ? `${pct(recovered / totals.costMonth)} of cost recovered` : 'No costs set yet'}
            />
            <StatCard
              tone={totals.profit < 0 ? 'danger' : 'success'}
              label="Profit / month"
              value={money(totals.profit)}
              sub={totals.margin == null ? 'Nothing billed yet' : `${pct(totals.margin)} margin on ${money(totals.billed)} billed`}
            />
          </>
        )}
        <StatCard
          label="Documents"
          value={docTotals.expired ? `${docTotals.expired} expired` : docTotals.expiring ? `${docTotals.expiring} expiring` : docTotals.count ? 'All valid' : 'None yet'}
          sub={docTotals.expired && docTotals.expiring
            ? `${docTotals.expiring} more expiring in 30 days`
            : plural(docTotals.count, 'file on record', 'files on record')}
        />
      </div>

      <BatchBar count={selection.selectedIds.size} onClear={selection.clear}>
        {isPeople ? (
          <>
            {canEditPermissions && selectedEmployeeIds.length > 0 && teamRoles.length > 0 && (
              <CustomDropdown
                toolbar
                value=""
                options={[{ value: '', label: 'Change role…' }, ...teamRoles.map((r) => ({ value: String(r.role_id), label: r.role_name }))]}
                onChange={(v) => v && changePeopleRole(v)}
              />
            )}
            {canInvitePeople && selectedInviteIds.length > 0 && (
              <button type="button" onClick={resendSelectedInvites}>Resend invite</button>
            )}
            {canDeletePeople && selectedEmployeeIds.length > 0 && (
              <button type="button" onClick={archivePeople}>{f.archived ? 'Restore' : 'Archive'}</button>
            )}
          </>
        ) : (
          <>
            {canEditResources && (
              <CustomDropdown
                toolbar
                value=""
                options={[{ value: '', label: 'Change status…' }, ...RESOURCE_STATUSES]}
                onChange={(v) => v && batchResources({ availability_status: v }, `set to ${resourceStatusLabel(v)}`)}
              />
            )}
            {canEditResources && (
              <CustomDropdown
                toolbar
                value=""
                options={[{ value: '', label: 'Assign to…' }, { value: 'none', label: 'Nobody' }, ...assignableOptions]}
                onChange={(v) => v && batchResources(
                  { assigned_employee_id: v === 'none' ? null : Number(v) },
                  v === 'none' ? 'unassigned' : `assigned to ${assignableOptions.find((o) => o.value === v)?.label}`,
                )}
              />
            )}
            {canDeleteResources && (
              <button type="button" onClick={() => batchResources({ is_active: f.archived }, f.archived ? 'restored' : 'archived')}>
                {f.archived ? 'Restore' : 'Archive'}
              </button>
            )}
          </>
        )}
      </BatchBar>

      {rows.length === 0 ? (
        <p className={peopleStyles.emptySubsection} style={{ padding: '24px 20px' }}>{emptyMessage}</p>
      ) : viewMode === 'list' ? (
        <DataTable
          key={activeTab}
          columns={shownColumns}
          data={rows}
          storageKey={isPeople ? 'peopleColumns' : 'resourceColumns'}
          groupedBy={group ? group.get : null}
          groupOrder={group?.order || []}
          getGroupColor={groupColor}
          collapsedGroups={collapsedGroups[activeTab]}
          toggleGroupCollapse={(g) => setCollapsedGroups((prev) => ({
            ...prev,
            [activeTab]: { ...prev[activeTab], [g]: !prev[activeTab][g] },
          }))}
          sortColumn={f.sort?.key}
          sortDirection={f.sort?.dir || 'asc'}
          onSort={handleSort}
          onRowClick={isPeople ? openEmployeeDetail : openResourceDetail}
          onToggleColumn={columnState.toggleColumn}
          onMoveColumn={columnState.moveColumn}
          onAddDetail={canManageCompanySettings ? () => setAddFieldEntity(isPeople ? 'employee' : 'resource') : null}
          selectable={canSelect}
          selectedIds={selection.selectedIds}
          getRowId={(row) => row.id}
          onToggleRow={selection.toggleRow}
          onToggleGroup={selection.toggleGroup}
          onToggleAll={(checked) => selection.toggleAll(rows.map((r) => r.id), checked)}
          customCellRender={isPeople ? personCells : resourceCells}
        />
      ) : (
        <div style={{ padding: '12px 20px' }}>
          {gridGroups.map(([name, groupRows]) => {
            const ids = groupRows.map((r) => r.id);
            const allSelected = ids.every((id) => selection.selectedIds.has(id));
            return (
              <React.Fragment key={name || 'all'}>
                {name && (
                  <div className={peopleStyles.gridGroupHeader}>
                    {canSelect && (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => selection.toggleGroup(ids, !allSelected)}
                        aria-label={`Select all in ${name}`}
                      />
                    )}
                    {groupColor && <span className={ui.groupColorDot} style={{ backgroundColor: groupColor(name) }} />}
                    <span className={ui.groupName}>{name}</span>
                    <span className={ui.groupCount}>{groupRows.length}</span>
                  </div>
                )}
                <div className={peopleStyles.cardGrid}>
                  {groupRows.map((row) => (isPeople ? (
                    <PersonCompactCard
                      key={row.id}
                      employee={row}
                      onClick={() => openEmployeeDetail(row)}
                      onAddContactInfo={canEditPeople && !isInvite(row) ? editEmployeeDirect : null}
                      selectable={canSelect}
                      selected={selection.selectedIds.has(row.id)}
                      onToggleSelect={() => selection.toggleRow(row.id)}
                    />
                  ) : (
                    <ResourceCompactCard
                      key={row.id}
                      resource={row}
                      onClick={() => openResourceDetail(row)}
                      selectable={canSelect}
                      selected={selection.selectedIds.has(row.id)}
                      onToggleSelect={() => selection.toggleRow(row.id)}
                    />
                  )))}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {addFieldEntity && (
        <CustomFieldModal
          defaultEntity={addFieldEntity}
          onClose={() => setAddFieldEntity(null)}
          onSaved={async () => {
            const data = await getCustomFields(addFieldEntity);
            (addFieldEntity === 'employee' ? setPersonFields : setResourceFields)(data.fields || []);
          }}
          onMessage={setToast}
        />
      )}

      {/* Invite Modal */}
      {showInviteForm && (
        <FormModal
          title="Invite Employee"
          onCancel={() => {
            setShowInviteForm(false);
            setForm({
              firstName: "",
              lastName: "",
              email: "",
              phone_number: "",
              default_hourly_rate: "",
              default_weekly_hours: "",
              title_id: "",
              permissions: [],
            });
          }}
          onSubmit={handleInvite}
          fields={[
            {
              type: "row",
              fields: [
                {
                  label: "First Name",
                  value: form.firstName,
                  onChange: (e) =>
                    setForm((prev) => ({ ...prev, firstName: e.target.value })),
                },
                {
                  label: "Last Name",
                  value: form.lastName,
                  onChange: (e) =>
                    setForm((prev) => ({ ...prev, lastName: e.target.value })),
                },
              ],
            },
            {
              label: "Email",
              type: "email",
              value: form.email,
              onChange: (e) =>
                setForm((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Job Title",
              type: "select",
              value: form.title_id,
              onChange: (e) => {
                if (e.target.value === "add-new-job-title") {
                  setJobTitleTarget('invite');
                  setShowNewJobTitleModal(true);
                } else {
                  setForm((prev) => ({ ...prev, title_id: e.target.value }));
                }
              },
              options: [
                { value: "", label: "Select a job title..." },
                { value: "add-new-job-title", label: "➕ Add New Job Title..." },
                ...(jobTitles || []).map((jt) => ({
                  value: jt.title_id,
                  label: jt.name,
                })),
              ],
            },
            ...(canEditPermissions && teamRoles.length > 0 ? [{
              label: "Team Role (Optional)",
              type: "select",
              value: "",
              onChange: (e) => handleTeamRoleChange(e.target.value),
              options: [
                { value: "", label: "Select a role to apply permissions..." },
                { value: "create-new-role", label: "➕ Create New Role..." },
                ...(teamRoles || []).map((role) => ({
                  value: role.role_id,
                  label: `${role.role_name}${role.is_default ? ' (Default)' : ''} - ${role.permissions?.length || 0} permissions`,
                })),
              ],
            }] : []),
            ...(canEditPermissions ? [{
              type: "custom",
              render: () => (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedPermissions(!showAdvancedPermissions)}
                    className={ui.advancedPermissionsToggle}
                  >
                    {showAdvancedPermissions ? '▼' : '▶'}
                    <span>Advanced Permissions - {showAdvancedPermissions ? 'Hide' : 'Show'} Manual Selection</span>
                  </button>
                  {showAdvancedPermissions && (
                    <PermissionSelector
                      permissions={availablePermissions}
                      selectedPermissions={form.permissions}
                      onChange={(newPermissions) =>
                        setForm((prev) => ({ ...prev, permissions: newPermissions }))
                      }
                    />
                  )}
                </div>
              ),
            }] : []),
          ]}
        />
      )}

      {/* Placeholder Modal */}
      {showPlaceholderModal && (
        <FormModal
          title="Add Contractor"
          onCancel={() => {
            setShowPlaceholderModal(false);
            setPlaceholderForm({
              name: "",
              phone_number: "",
              description: "",
              default_hourly_rate: "",
              default_weekly_hours: "",
            });
          }}
          onSubmit={handleCreatePlaceholder}
          fields={[
            {
              label: "Name",
              value: placeholderForm.name,
              onChange: (e) =>
                setPlaceholderForm((prev) => ({ ...prev, name: e.target.value })),
              placeholder: "Enter contractor name...",
            },
            {
              label: "Phone Number",
              type: "tel",
              value: placeholderForm.phone_number,
              onChange: (e) =>
                setPlaceholderForm((prev) => ({ ...prev, phone_number: e.target.value })),
              placeholder: "+27 12 345 6789",
            },
            {
              label: "Description",
              type: "textarea",
              value: placeholderForm.description,
              onChange: (e) =>
                setPlaceholderForm((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Enter description...",
            },
            {
              type: "row",
              fields: [
                {
                  label: `Hourly Rate (${currencySymbol})`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: placeholderForm.default_hourly_rate,
                  onChange: (e) =>
                    setPlaceholderForm((prev) => ({ ...prev, default_hourly_rate: e.target.value })),
                },
                {
                  label: "Weekly Hours",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  placeholder: "40",
                  value: placeholderForm.default_weekly_hours,
                  onChange: (e) =>
                    setPlaceholderForm((prev) => ({ ...prev, default_weekly_hours: e.target.value })),
                },
              ],
            },
          ]}
        />
      )}

      {/* Create New Role Modal */}
      {showCreateRoleModal && (
        <FormModal
          title="Create New Team Role"
          onCancel={() => {
            setShowCreateRoleModal(false);
            setNewRoleForm({
              role_name: "",
              description: "",
              permissions: [],
            });
          }}
          onSubmit={handleCreateNewRole}
          fields={[
            {
              label: "Role Name",
              value: newRoleForm.role_name,
              onChange: (e) =>
                setNewRoleForm((prev) => ({ ...prev, role_name: e.target.value })),
              placeholder: "e.g., Project Manager, Developer, Viewer",
            },
            {
              label: "Description",
              type: "textarea",
              value: newRoleForm.description,
              onChange: (e) =>
                setNewRoleForm((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Describe this role and when to use it",
            },
            {
              label: "Permissions",
              type: "custom",
              render: () => (
                <PermissionSelector
                  permissions={availablePermissions}
                  selectedPermissions={newRoleForm.permissions}
                  onChange={(newPermissions) =>
                    setNewRoleForm((prev) => ({ ...prev, permissions: newPermissions }))
                  }
                />
              ),
            },
          ]}
        />
      )}

      {editingPlaceholder && (
        <FormModal
          title="Edit Contractor"
          onCancel={() => {
            setEditingPlaceholder(null);
            setPlaceholderTab('details');
          }}
          onSubmit={placeholderTab === 'details' ? handleUpdatePlaceholder : null}
          tabs={[
            { key: 'details', label: 'Details' },
            ...(canViewProjectBudgets ? [{ key: 'costs', label: 'Cost & profit' }] : []),
            { key: 'documents', label: 'Documents' },
          ]}
          activeTab={placeholderTab}
          onTabChange={setPlaceholderTab}
          customTabContent={placeholderTab === 'costs' ? (
            <CostBreakdown entity={editingPlaceholder} kind="person" formatMoney={formatMoney} />
          ) : placeholderTab === 'documents' ? (
            <DocumentsPanel entityType="employee" entityId={editingPlaceholder.user_id} canEdit={canEditPeople} onChange={refreshEmployees} />
          ) : null}
          fields={[
            {
              label: "Name",
              value: editingPlaceholder.name,
              onChange: (e) =>
                setEditingPlaceholder((prev) => ({ ...prev, name: e.target.value })),
              placeholder: "Enter contractor name...",
            },
            {
              label: "Phone Number",
              type: "tel",
              value: editingPlaceholder.phone_number || "",
              onChange: (e) =>
                setEditingPlaceholder((prev) => ({ ...prev, phone_number: e.target.value })),
              placeholder: "+27 12 345 6789",
            },
            {
              label: "Description",
              type: "textarea",
              value: editingPlaceholder.description || "",
              onChange: (e) =>
                setEditingPlaceholder((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Enter description...",
            },
            {
              type: "row",
              fields: [
                {
                  label: `Hourly Rate (${currencySymbol})`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: editingPlaceholder.default_hourly_rate || "",
                  onChange: (e) =>
                    setEditingPlaceholder((prev) => ({ ...prev, default_hourly_rate: e.target.value })),
                },
                {
                  label: "Weekly Hours",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  placeholder: "40",
                  value: editingPlaceholder.default_weekly_hours || "",
                  onChange: (e) =>
                    setEditingPlaceholder((prev) => ({ ...prev, default_weekly_hours: e.target.value })),
                },
                {
                  label: `Rate to clients (${currencySymbol}/h)`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: editingPlaceholder.bill_rate || "",
                  onChange: (e) =>
                    setEditingPlaceholder((prev) => ({ ...prev, bill_rate: e.target.value })),
                },
              ],
            },
            departmentField(editingPlaceholder, setEditingPlaceholder),
            ...(personFields.length > 0 ? [{
              type: "custom",
              render: () => (
                <CustomFieldRows fields={personFields} entity={editingPlaceholder} disabled={!canEditPeople} formatMoney={formatMoney} onSave={(field, value) => saveEmployeeField(editingPlaceholder, field, value)} />
              ),
            }] : []),
          ]}
        />
      )}

      {editingEmployee && canEditPeople && (
        <FormModal
          title="Edit Employee"
          onCancel={() => {
            setEditingEmployee(null);
            setAvatarFile(null);
            setEditTab('details');
            setShowAdvancedPermissions(false);
          }}
          onSubmit={editTab === 'details' ? handleUpdateEmployee : null}
          tabs={[
            { key: 'details', label: 'Details' },
            ...(canAccessTimeOff ? [{ key: 'timeoff', label: 'Time Off' }] : []),
            ...(canViewProjectBudgets ? [{ key: 'costs', label: 'Cost & profit' }] : []),
            { key: 'projects', label: 'Projects' },
            { key: 'documents', label: 'Documents' },
          ]}
          activeTab={editTab}
          onTabChange={handleEditTabChange}
          customTabContent={editTab === 'costs' ? (
            <CostBreakdown entity={editingEmployee} kind="person" formatMoney={formatMoney} />
          ) : editTab === 'projects' ? (
            employeeProjects === null ? (
              <p className={peopleStyles.emptySubsection}>Loading projects…</p>
            ) : employeeProjects.length === 0 ? (
              <p className={peopleStyles.emptySubsection}>Not on any active projects.</p>
            ) : (
              <ul className={peopleStyles.projectList}>
                {employeeProjects.map((proj) => (
                  <li key={proj.project_id}>
                    <span className={ui.groupColorDot} style={{ backgroundColor: proj.status_color || '#94a3b8' }} />
                    <strong>{proj.project_name}</strong>
                    <span className={chrome.cellSub}>
                      {[proj.company_name, proj.status_name, proj.employee_role].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : editTab === 'documents' ? (
            <DocumentsPanel entityType="employee" entityId={editingEmployee.user_id} canEdit={canEditPeople} onChange={refreshEmployees} />
          ) : editTab === 'timeoff' ? (
            <div>
              {/* Summary Cards */}
              {entitlements.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
                  {[
                    { label: 'Available', value: entitlements.reduce((s, e) => s + parseFloat(e.total_available || 0), 0), color: '#6366f1' },
                    { label: 'Approved', value: entitlements.reduce((s, e) => s + parseFloat(e.days_approved || 0), 0), color: '#16a34a' },
                    { label: 'Pending', value: entitlements.reduce((s, e) => s + parseFloat(e.days_pending || 0), 0), color: '#d97706' },
                    { label: 'Remaining', value: entitlements.reduce((s, e) => s + parseFloat(e.days_remaining || 0), 0), color: '#2563eb' },
                  ].map((card) => (
                    <div key={card.label} style={{ textAlign: 'center', padding: '10px 6px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '700', color: card.color }}>{card.value}</div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>{card.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={handleGenerateDefaults}
                  style={{ padding: '6px 12px', fontSize: '0.8rem', backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Generate Defaults
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddEntitlementModal(true)}
                  style={{ padding: '6px 12px', fontSize: '0.8rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
                >
                  + Add Entitlement
                </button>
              </div>

              {/* Entitlement Table */}
              {entitlementsLoading ? (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Loading entitlements...</p>
              ) : entitlements.length === 0 ? (
                <p style={{ color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                  No leave entitlements found. Click "Generate Defaults" to create entitlements from company leave types.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase' }}>Leave Type</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Total</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Carry Over</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Approved</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Pending</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Remaining</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entitlements.map((ent) => {
                        const remaining = parseFloat(ent.days_remaining || 0);
                        const total = parseFloat(ent.total_available || 0);
                        const isLow = total > 0 && remaining / total < 0.2;
                        return (
                          <tr key={ent.entitlement_id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: isLow ? '#fef2f2' : 'transparent' }}>
                            <td style={{ padding: '8px 6px', fontWeight: '500', color: '#111827' }}>
                              {ent.time_off_type_name}
                              <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                {new Date(ent.period_start).toLocaleDateString()} → {new Date(ent.period_end).toLocaleDateString()}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', padding: '8px 4px' }}>{parseFloat(ent.total_days)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px' }}>{parseFloat(ent.carry_over_days)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px', color: '#16a34a' }}>{parseFloat(ent.days_approved)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px', color: '#d97706' }}>{parseFloat(ent.days_pending)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px', fontWeight: '600', color: isLow ? '#dc2626' : '#2563eb' }}>{remaining}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                              <button
                                type="button"
                                onClick={() => setEditingEntitlement({ ...ent, period_start: ent.period_start?.split('T')[0], period_end: ent.period_end?.split('T')[0] })}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: '0.8rem', marginRight: '4px' }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteEntitlement(ent.entitlement_id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.8rem' }}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
          fields={[
            {
              label: "Avatar",
              type: "custom",
              render: () => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    border: '2px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {editingEmployee.avatar_url ? (
                      <img 
                        src={resolveUploadUrl(editingEmployee.avatar_url)}
                        alt={editingEmployee.name}
                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      editingEmployee.name.split(' ').map(n => n[0]).join('').toUpperCase()
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setAvatarFile(e.target.files[0])}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '12px', 
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      Select a new image to update the avatar (max 2MB)
                    </p>
                  </div>
                </div>
              ),
            },
            {
              type: "row",
              fields: [
                {
                  label: "First Name",
                  value: editingEmployee.firstName,
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, firstName: e.target.value })),
                },
                {
                  label: "Last Name",
                  value: editingEmployee.lastName,
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, lastName: e.target.value })),
                },
              ],
            },
            {
              label: "Email",
              type: "email",
              value: editingEmployee.email,
              onChange: (e) =>
                setEditingEmployee((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Phone Number",
              type: "tel",
              value: editingEmployee.phone_number || "",
              placeholder: "+27 123 4567",
              onChange: (e) =>
                setEditingEmployee((prev) => ({ ...prev, phone_number: e.target.value })),
            },
            {
              label: "Job Title",
              type: "select",
              value: editingEmployee.title_id,
              onChange: (e) => {
                if (e.target.value === "add-new-job-title") {
                  setJobTitleTarget('edit');
                  setShowNewJobTitleModal(true);
                } else {
                  setEditingEmployee((prev) => ({ ...prev, title_id: e.target.value }));
                }
              },
              options: [
                { value: "add-new-job-title", label: "➕ Add New Job Title..." },
                ...(jobTitles || []).map((jt) => ({
                  value: jt.title_id,
                  label: jt.name,
                })),
              ],
            },
            departmentField(editingEmployee, setEditingEmployee),
            {
              type: "row",
              fields: [
                {
                  label: `Cost to company (${currencySymbol}/h)`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: editingEmployee.default_hourly_rate || "",
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, default_hourly_rate: e.target.value })),
                },
                {
                  label: `Rate to clients (${currencySymbol}/h)`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: editingEmployee.bill_rate || "",
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, bill_rate: e.target.value })),
                },
                {
                  label: "Weekly Hours",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  placeholder: "40",
                  value: editingEmployee.default_weekly_hours || "",
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, default_weekly_hours: e.target.value })),
                },
              ],
            },
            ...(canEditPermissions ? [{
              label: "Role",
              type: "select",
              value: editingEmployee.team_role_id || "",
              onChange: (e) => handleEditTeamRoleChange(e.target.value),
              options: [
                { value: "", label: "Select a role to apply permissions..." },
                { value: "create-new-role", label: "+ Add role…" },
                ...(teamRoles || []).map((role) => ({
                  value: role.role_id,
                  label: `${role.role_name}${role.is_default ? ' (Default)' : ''} - ${role.permissions?.length || 0} permissions`,
                })),
              ],
            }] : []),
            ...(canEditPermissions ? [{
              type: "custom",
              render: () => (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedPermissions(!showAdvancedPermissions)}
                    className={ui.advancedPermissionsToggle}
                  >
                    {showAdvancedPermissions ? '▼' : '▶'}
                    <span>Advanced Permissions - {showAdvancedPermissions ? 'Hide' : 'Show'} Manual Selection</span>
                  </button>
                  {showAdvancedPermissions && (
                    <PermissionSelector
                      permissions={availablePermissions}
                      selectedPermissions={editingEmployee.permissions || []}
                      onChange={(newPermissions) =>
                        setEditingEmployee((prev) => ({ ...prev, permissions: newPermissions }))
                      }
                    />
                  )}
                </div>
              ),
            }] : []),
            ...(personFields.length > 0 ? [{
              type: "custom",
              render: () => (
                <CustomFieldRows fields={personFields} entity={editingEmployee} disabled={!canEditPeople} formatMoney={formatMoney} onSave={(field, value) => saveEmployeeField(editingEmployee, field, value)} />
              ),
            }] : []),
          ]}
        />
      )}

      {/* Employee Detail Modal */}
      {showDetailModal && selectedEmployee && (
        <EmployeeDetailModal
          employee={selectedEmployee}
          getJobTitleName={getJobTitleName}
          canEditPeople={canEditPeople}
          canDeletePeople={canDeletePeople}
          canInvitePeople={canInvitePeople}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedEmployee(null);
          }}
          onEditEmployee={handleEditFromDetail}
          onEditPlaceholder={handleEditPlaceholderFromDetail}
          onDeleteClick={() => setShowDeleteConfirm(true)}
          onCancelInviteClick={() => setShowCancelInviteConfirm(true)}
        />
      )}

      {/* Add Entitlement Modal */}
      {showAddEntitlementModal && (
        <FormModal
          title="Add Leave Entitlement"
          onCancel={() => setShowAddEntitlementModal(false)}
          onSubmit={handleAddEntitlement}
          fields={[
            {
              label: "Leave Type",
              type: "select",
              value: entitlementForm.time_off_type_id,
              onChange: (e) => {
                if (e.target.value === '__add_new__') {
                  setShowNewLeaveTypeModal(true);
                } else {
                  setEntitlementForm(prev => ({ ...prev, time_off_type_id: e.target.value }));
                }
              },
              options: [
                { value: '', label: 'Select leave type...' },
                ...timeOffTypes.map(t => ({ value: t.time_off_type_id, label: t.name })),
                { value: '__add_new__', label: '➕ Add New Leave Type...' }
              ]
            },
            {
              type: "row",
              fields: [
                {
                  label: "Total Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: entitlementForm.total_days,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, total_days: e.target.value })),
                },
                {
                  label: "Carry Over Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: entitlementForm.carry_over_days,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, carry_over_days: e.target.value })),
                },
              ],
            },
            {
              type: "row",
              fields: [
                {
                  label: "Period Start",
                  type: "date",
                  value: entitlementForm.period_start,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, period_start: e.target.value })),
                },
                {
                  label: "Period End",
                  type: "date",
                  value: entitlementForm.period_end,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, period_end: e.target.value })),
                },
              ],
            },
            {
              label: "Notes",
              type: "textarea",
              value: entitlementForm.notes,
              onChange: (e) => setEntitlementForm(prev => ({ ...prev, notes: e.target.value })),
              placeholder: "Optional notes...",
            },
          ]}
        />
      )}

      {/* Edit Entitlement Modal */}
      {editingEntitlement && (
        <FormModal
          title="Edit Leave Entitlement"
          onCancel={() => setEditingEntitlement(null)}
          onSubmit={handleUpdateEntitlement}
          fields={[
            {
              label: "Leave Type",
              type: "text",
              value: editingEntitlement.time_off_type_name || '',
              onChange: () => {},
              disabled: true,
            },
            {
              type: "row",
              fields: [
                {
                  label: "Total Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: editingEntitlement.total_days,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, total_days: e.target.value })),
                },
                {
                  label: "Carry Over Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: editingEntitlement.carry_over_days,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, carry_over_days: e.target.value })),
                },
              ],
            },
            {
              type: "row",
              fields: [
                {
                  label: "Period Start",
                  type: "date",
                  value: editingEntitlement.period_start,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, period_start: e.target.value })),
                },
                {
                  label: "Period End",
                  type: "date",
                  value: editingEntitlement.period_end,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, period_end: e.target.value })),
                },
              ],
            },
            {
              label: "Notes",
              type: "textarea",
              value: editingEntitlement.notes || '',
              onChange: (e) => setEditingEntitlement(prev => ({ ...prev, notes: e.target.value })),
              placeholder: "Optional notes...",
            },
          ]}
        />
      )}

      {/* New Leave Type Modal */}
      {showNewLeaveTypeModal && (
        <FormModal
          title="Add New Leave Type"
          onCancel={() => {
            setShowNewLeaveTypeModal(false);
            setNewLeaveTypeForm({ name: '', description: '', default_allocation: '' });
          }}
          onSubmit={handleCreateLeaveType}
          fields={[
            {
              label: "Name",
              type: "text",
              value: newLeaveTypeForm.name,
              onChange: (e) => setNewLeaveTypeForm(prev => ({ ...prev, name: e.target.value })),
              placeholder: "e.g. Annual Leave, Sick Leave",
            },
            {
              label: "Default Allocation (days/year)",
              type: "number",
              min: "0",
              step: "1",
              value: newLeaveTypeForm.default_allocation,
              onChange: (e) => setNewLeaveTypeForm(prev => ({ ...prev, default_allocation: e.target.value })),
              placeholder: "e.g. 25",
            },
            {
              label: "Description",
              type: "textarea",
              value: newLeaveTypeForm.description,
              onChange: (e) => setNewLeaveTypeForm(prev => ({ ...prev, description: e.target.value })),
              placeholder: "Optional description...",
            },
          ]}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedEmployee && (
        <ConfirmDialog
          message={
            selectedEmployee.kind === 'ghost'
              ? `Archive ${selectedEmployee.name}? Their timesheets and bookings are kept and they can be restored later, but they'll be removed from active lists and excluded from billing.`
              : `Archive ${selectedEmployee.name}? Their timesheets, bookings and history are kept and they can be restored later, but they'll be removed from active lists, excluded from billing, and unable to log in.`
          }
          confirmText="Archive"
          cancelText="Cancel"
          onConfirm={handleDeleteEmployee}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Cancel Invite Confirmation */}
      {showCancelInviteConfirm && selectedEmployee && (
        <ConfirmDialog
          message={`Are you sure you want to cancel the invitation for ${selectedEmployee.name}?`}
          confirmText="Cancel Invitation"
          cancelText="Keep Invitation"
          onConfirm={handleCancelInvite}
          onCancel={() => setShowCancelInviteConfirm(false)}
        />
      )}

      {/* New Job Title Modal */}
      {showNewJobTitleModal && (
        <FormModal
          title="Add New Job Title"
          onCancel={() => {
            setShowNewJobTitleModal(false);
            setNewJobTitleName("");
            setJobTitleTarget(null);
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleCreateJobTitle();
          }}
          fields={[
            {
              label: "Job Title Name(s)",
              type: "textarea",
              value: newJobTitleName,
              onChange: (e) => setNewJobTitleName(e.target.value),
              placeholder: "Enter one job title per line...",
              note: "Add multiple titles by entering one per line.",
              required: true,
            },
          ]}
        />
      )}

      {/* Resource Create Modal */}
      {showResourceModal && (
        <FormModal
          title="Create Resource"
          onCancel={() => {
            setShowResourceModal(false);
            resetResourceForm();
          }}
          onSubmit={handleCreateResource}
          fields={getResourceFormFields(resourceForm, setResourceForm)}
        />
      )}

      {/* Resource Edit Modal */}
      {editingResource && canEditResources && (
        <FormModal
          title="Edit Resource"
          onCancel={() => { setEditingResource(null); setResourceTab('details'); }}
          onSubmit={resourceTab === 'details' ? handleUpdateResource : null}
          tabs={[
            { key: 'details', label: 'Details' },
            { key: 'bookings', label: 'Bookings' },
            ...(canViewProjectBudgets ? [{ key: 'costs', label: 'Cost & profit' }] : []),
            { key: 'documents', label: 'Documents' },
          ]}
          activeTab={resourceTab}
          onTabChange={handleResourceTabChange}
          customTabContent={resourceTab === 'bookings' ? (
            resourceBookings === null ? (
              <p className={peopleStyles.emptySubsection}>Loading bookings…</p>
            ) : resourceBookings.length === 0 ? (
              <p className={peopleStyles.emptySubsection}>No bookings in the next 60 days.</p>
            ) : (
              <ul className={peopleStyles.projectList}>
                {resourceBookings.map((b) => (
                  <li key={b.id}>
                    <strong>{new Date(b.start_datetime).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</strong>
                    <span className={chrome.cellSub}>
                      {[users.find((u) => u.user_id === Number(b.employee_id))?.name, b.project_name, b.notes].filter(Boolean).join(' · ') || 'Booked'}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : resourceTab === 'costs' ? (
            <CostBreakdown entity={editingResource} kind="resource" formatMoney={formatMoney} />
          ) : resourceTab === 'documents' ? (
            <DocumentsPanel entityType="resource" entityId={editingResource.resource_id} canEdit={canEditResources} onChange={refreshResources} />
          ) : null}
          fields={getResourceFormFields(editingResource, setEditingResource)}
        />
      )}

      {departmentTarget && (
        <FormModal
          title="Add Department"
          onCancel={() => { setDepartmentTarget(null); setNewDepartmentName(""); }}
          onSubmit={handleCreateDepartment}
          fields={[{
            label: "Department name",
            value: newDepartmentName,
            onChange: (e) => setNewDepartmentName(e.target.value),
            placeholder: "e.g. Design, Site crew",
          }]}
        />
      )}

      {/* Resource Detail Modal */}
      {showResourceDetailModal && selectedResource && (
        <ResourceDetailModal
          resource={selectedResource}
          canEditResources={canEditResources}
          canDeleteResources={canDeleteResources}
          getResourceTypeLabel={resourceTypeLabel}
          getAvailabilityLabel={resourceStatusLabel}
          getAvailabilityColor={resourceStatusColor}
          onClose={() => {
            setShowResourceDetailModal(false);
            setSelectedResource(null);
          }}
          onEditClick={handleEditResourceFromDetail}
          onArchiveClick={() => setShowArchiveConfirm(true)}
          onRestoreClick={() => handleRestoreResource(selectedResource)}
        />
      )}

      {/* Archive Confirmation */}
      {showArchiveConfirm && selectedResource && (
        <ConfirmDialog
          message={`Are you sure you want to archive "${selectedResource.name}"? It will be excluded from active workflows but can be restored later.`}
          confirmText="Archive"
          cancelText="Cancel"
          onConfirm={handleArchiveResource}
          onCancel={() => setShowArchiveConfirm(false)}
        />
      )}
      </div>
    </div>
  );
}
